import * as React from "react"
import { Form, Button, Col, Spinner } from "react-bootstrap"
import { Subscription } from "rxjs"
import { toast } from "react-toastify"
import { FaPencilAlt, FaTimes } from "react-icons/fa"
import { Day } from "../../../api/Randoms/GovPoolModel/DayOfWeek"
import GovPoolUpdateObservable from "../../../api/Randoms/GovPoolUpdate"
import { WeekdaysToExclude } from "./WeekdaysToExclude"
import ActionButton from "../../ActionButton"
import ConfirmationModal from "../../ConfirmationModal"
import GridRow from "../../../api/Randoms/GovPoolModel/GridRow"
import { SampleType, GovPanel } from "../../../api/Randoms/GovPoolModel/CollectionTypeResponse"
import GovDevice from "../../../api/Randoms/GovPoolModel/GovDevice"
import LabAccount from "../../../api/LabService/LabServiceModel/LabAccount"
import GovCollection from "../../OrderRequests/GovCollection"
import { DaysToExcludeExt } from "./RandomSchedulingExclusionsModal"
import GetDaysToExclude from "../../../api/Randoms/GovDaysToExclude"
import DatesToExclude from "./DatesToExclude"
import DatesToExcludeModal from "./DatesToExcludeModal"

interface GovPoolGeneralInfoProps {
  details: GridRow
  onUpdate: (updated: boolean) => void
}

interface GovPoolGeneralInfoState {
  data: GridRow
  editing: boolean
  saving: boolean
  loadingDaysExcluded: boolean
  showSaveConfirmModal: boolean

  sampleTypeID?: number
  isPOCTDevice?: boolean
  labAccount?: string
  panelID?: string
  deviceCode?: string

  daysExcluded: DaysToExcludeExt[]
}

export default class GovPoolGeneralInfo extends React.PureComponent<GovPoolGeneralInfoProps, GovPoolGeneralInfoState> {
  state: GovPoolGeneralInfoState = {
    data: { ...this.props.details },
    sampleTypeID: this.props.details.SampleTypeID,
    labAccount: this.props.details.LaboratoryAccount,
    panelID: this.props.details.DefaultPanel,
    deviceCode: this.props.details.DefaultDevice,
    editing: false,
    saving: false,
    loadingDaysExcluded: false,
    showSaveConfirmModal: false,
    daysExcluded: [...this.props.details.DaysExcluded]
  }

  updateSubscription?: Subscription
  loadDaysExcludedSubscription?: Subscription

  componentDidMount() {

  }

  componentWillUnmount() {
    this.updateSubscription?.unsubscribe()
    this.loadDaysExcludedSubscription?.unsubscribe()
  }

  componentDidUpdate(prevProps: GovPoolGeneralInfoProps, prevState: GovPoolGeneralInfoState) {
    if (this.state.data !== prevState.data && this.state.loadingDaysExcluded === prevState.loadingDaysExcluded) {
      this.setState({ loadingDaysExcluded: true })
      this.loadDaysExcludedSubscription?.unsubscribe()
      this.loadDaysExcludedSubscription = GetDaysToExclude(this.state.data.PoolID).subscribe(
        result => {
          this.setState(prevState => {
            return {
              ...prevState,
              data: { ...prevState.data, DaysExcluded: result },
              daysExcluded: result,
              loadingDaysExcluded: false
            }
          })
        },
        error => {
          this.setState({ loadingDaysExcluded: false })
          toast.error(error?.errorData?.message || error.message)
        }
      )
    }
  }

  handleToggleEditMode = () => {
    if (this.state.editing) {
      this.setState({
        sampleTypeID: this.state.data.SampleTypeID,
        labAccount: this.state.data.LaboratoryAccount,
        panelID: this.state.data.DefaultPanel,
        deviceCode: this.state.data.DefaultDevice,
        daysExcluded: this.state.data.DaysExcluded,
        isPOCTDevice: this.state.isPOCTDevice
      })
    }
    this.setState({ editing: !this.state.editing })
  }

  handleTypeChange = (type?: SampleType, isPOCTDevice?: boolean) => this.setState({ sampleTypeID: type?.SampleTypeID, isPOCTDevice: isPOCTDevice })
  handleLabChange = (lab?: LabAccount) => this.setState({ labAccount: lab?.LabAccount })
  handlePanelChange = (panel?: GovPanel) => this.setState({ panelID: panel?.ID })
  handleDeviceChange = (device?: GovDevice) => this.setState({ deviceCode: device?.Code.toString() })

  handleWeekdayToExcludeChange = (event: React.ChangeEvent<HTMLInputElement>, day: Day) => {
    const checked = event.target.checked
    const list = [...this.state.daysExcluded]
    const index = list.findIndex(item => item.DayOfWeekID === day)
    if (index === -1) {
      if (checked) {
        list.push({ ImplementationID: 0, AccountID: 0, DaysToExcludeID: 0, DayOfWeekID: day })
      }
    } else {
      list[index] = { ...list[index], removed: !checked }
    }

    this.setState({ daysExcluded: list })
  }

  handleDaysToExcludeChange = (list: DaysToExcludeExt[]) => {
    const addedAndRemovedDays = list.filter(item => !(item.removed && item.DaysToExcludeID === 0))
    this.setState({
      daysExcluded: [...addedAndRemovedDays]
    })
  }

  handleSubmit = () => {
    if (!this.isValid()) {
      return
    }
    this.setState({ showSaveConfirmModal: true })
  }

  isValid = (): boolean => {
    const changes = [
      Number(this.state.sampleTypeID) > 0,
      !!this.state.labAccount,
      !!this.state.panelID,
      (!this.state.isPOCTDevice || (this.state.isPOCTDevice && !!this.state.deviceCode))
    ]
    return changes.every((valid: boolean) => valid)
  }

  performSave = () => {
    this.setState({ saving: true, showSaveConfirmModal: false })
    this.updateSubscription?.unsubscribe()
    const data = {
      ...this.state.data,
      SampleTypeID: Number(this.state.sampleTypeID),
      LaboratoryAccount: String(this.state.labAccount),
      DefaultPanel: String(this.state.panelID),
      DefaultDevice: String(this.state.deviceCode),
      DaysExcluded: this.state.daysExcluded.filter(item => !(item.removed && item.DaysToExcludeID === 0)).map(item => {
        const { removed, ...rest } = item
        return { ...rest, DoRemove: (item.DaysToExcludeID ?? 0) > 0 && removed }
      })
    }

    this.updateSubscription = GovPoolUpdateObservable(data).subscribe(
      () => {
        toast.success("Updated group details successfully.")
        this.setState({ saving: false, data: data })
        this.props.onUpdate(true)
        this.handleToggleEditMode()
      },
      () => {
        this.setState({ saving: false })
        toast.error("Error updating group details")
      }
    )
  }

  daysToExcludeWithoutRemoved = () => this.state.daysExcluded.filter(item => !item.removed)

  render() {
    const { editing, daysExcluded } = this.state

    return <>
      <Form className="p-4 pt-3">
        <Form.Row>
          <Col>{<h5>MODIFY GROUP CONFIGURATION</h5>}</Col>
          <Col xs={1}>
            <h5>
              <Button variant="icon-link" size="sm" className="float-right ml-1 btn-icon-primary" onClick={this.handleToggleEditMode}>
                {!editing && <FaPencilAlt />}
                {editing && <FaTimes />}
              </Button>
            </h5>
          </Col>
        </Form.Row>
        <Form.Row className={"form-group"}>
          <Form.Label column sm={5}>Group Name</Form.Label>
          <Col sm={7} className="d-flex align-items-center">
            {this.props.details.Name}
          </Col>
        </Form.Row>
        <Form.Row className={"form-group"}>
          <Form.Label column sm={5} htmlFor="selectionInterval">Selection Interval</Form.Label>
          <Col sm={7} className="d-flex align-items-center">
            {this.props.details.SelectionInterval}
          </Col>
        </Form.Row>
        <Form.Row className="form-group">
          <Form.Label column sm={5} htmlFor="numberOfSelections">Number of Selections / Interval </Form.Label>
          <Col sm={7} className="d-flex align-items-center">
            {this.props.details.NumberOfSelections} to {this.props.details.MaxNumberSelections || this.props.details.NumberOfSelections}
          </Col>
        </Form.Row>
        <GovCollection
          editing={editing}
          labAccount={this.state.labAccount}
          serviceDOT={false}
          sampleTypeID={this.state.sampleTypeID}
          panel={this.state.panelID}
          device={this.state.deviceCode}
          companyID={this.state.data.AccountID}
          onTypeChange={this.handleTypeChange}
          onLabChange={this.handleLabChange}
          onPanelChange={this.handlePanelChange}
          onDeviceChange={this.handleDeviceChange}
        />
        <Form.Row className="form-group">
          <Form.Label column sm={5} htmlFor="daysToExclude">Days of Week to Exclude</Form.Label>
          <Col className="d-flex align-items-center">
            {this.state.loadingDaysExcluded
              ? <Spinner animation="border" size="sm" />
              : <WeekdaysToExclude editable={editing} daysToExclude={this.daysToExcludeWithoutRemoved()} handlerChange={this.handleWeekdayToExcludeChange} />}
          </Col>
        </Form.Row>
        <Form.Row>
          <Form.Label column sm={3}>Dates to Exclude</Form.Label>
          <Col sm={2} className="align-items-top">
            {!this.state.loadingDaysExcluded && editing &&
              <DatesToExcludeModal
                classNameButton="float-right"
                poolID={this.state.data.PoolID}
                daysExcluded={this.state.daysExcluded}
                onSave={this.handleDaysToExcludeChange} />}
          </Col>
          <Col sm={7} className="align-items-center">
            <DatesToExclude daysToExclude={this.daysToExcludeWithoutRemoved()} />
          </Col>
        </Form.Row>
        <Form.Row className="buttons-block" hidden={!editing}>
          <Button variant="secondary" onClick={this.handleToggleEditMode}>CANCEL</Button>
          <ActionButton variant="primary" onClick={this.handleSubmit} spin={this.state.saving} disabled={!this.isValid()} disableOnSpin>SAVE</ActionButton>
        </Form.Row>
      </Form>
      <ConfirmationModal show={this.state.showSaveConfirmModal}
        title="CONFIRMATION"
        textTitle="Are you sure you want to submit?"
        text="You have made modification to this group configuration. Changes will be applied to the next random selection for this group."
        onConfirm={this.performSave}
        onHide={() => this.setState({ showSaveConfirmModal: false })} />
    </>
  }
}
